import { Button, Icon, Modal, Tabs, Typography } from '@globalmed/atlas-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styles from './CreateInstitutionModal.module.css';
import { PocTab, ProfileTab, ProvisioningTab } from './components';
import { createInstitutionSchema, TCreateInstitutionForm } from './schema';
import { defaultValues } from './defaultValues';
import { ORG_INSTITUTION_PROFILE_FIELDS } from '@/constants';
import { CloseWarningModal } from '@/components/CloseWarningModal';
import { useInstitutionsStore } from '@/stores/institutions';
import { useOrganizationsStore } from '@/stores/organizations';
import { useToastStore } from '@/stores/toast';
import { EOrganizationProvisioningScope } from '@/types/organizations';
import { EServerError } from '@/types/errorCodes';
import { ERouterNames } from '@/types/navigation';
import { createUnionGuard } from '@/utils/typeGuards';

type TProps = {
  open: boolean;
  onClose: () => void;
};

const CREATE_INSTITUTION_TABS = ['profile', 'poc', 'provisioning'] as const;
type TTabKey = (typeof CREATE_INSTITUTION_TABS)[number];

const isCreateInstitutionTab = createUnionGuard(CREATE_INSTITUTION_TABS);

/**
 * Modal for creating a new institution: collects profile, POC, and
 * provisioning details across three tabs, then shows a success screen.
 * @param props - Component props.
 * @param props.open - Controls whether the modal is visible.
 * @param props.onClose - Called when the modal should close.
 * @returns CreateInstitutionModal component.
 */
export const CreateInstitutionModal = ({ open, onClose }: TProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createInstitution, setCurrentInstitutionById } = useInstitutionsStore(
    (state) => ({
      createInstitution: state.createInstitution,
      setCurrentInstitutionById: state.setCurrentInstitutionById,
    }),
  );
  const currentOrganization = useOrganizationsStore(
    (state) => state.currentOrganization,
  );
  const currentOrganizationId = currentOrganization?.id;
  const { setToast } = useToastStore();

  const isInstitutionProvisioning =
    currentOrganization?.provisioning?.scope ===
    EOrganizationProvisioningScope.INSTITUTION;

  const [currentTab, setCurrentTab] = useState<TTabKey>('profile');
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);

  const schema = createInstitutionSchema(isInstitutionProvisioning);

  const formMethods = useForm<TCreateInstitutionForm>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onChange',
  });

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { isDirty, isSubmitting, errors },
  } = formMethods;

  const values = useWatch({ control });
  const invalidFields = new Set([
    ...(schema.safeParse(values).error?.issues.map(({ path }) => path[0]) ??
      []),
    ...Object.keys(errors),
  ]);

  const items = [
    {
      key: 'profile',
      title: t('admin-panel.profile'),
      alert: ORG_INSTITUTION_PROFILE_FIELDS.some((field) =>
        invalidFields.has(field),
      ),
    },
    {
      key: 'poc',
      title: t('create-organization-modal.poc'),
      alert: invalidFields.has('pocSettings'),
    },
    ...(isInstitutionProvisioning
      ? [
          {
            key: 'provisioning',
            title: t('create-organization-modal.provisioning'),
            alert: invalidFields.has('provisioning'),
          },
        ]
      : []),
  ];

  const resetAndClose = () => {
    reset(defaultValues);
    setCurrentTab('profile');
    setStep('form');
    onClose();
  };

  const handleCloseAttempt = () => {
    if (step === 'form' && isDirty) {
      setIsDiscardOpen(true);
      return;
    }
    resetAndClose();
  };

  const handleGetStarted = () => {
    resetAndClose();
    navigate(ERouterNames.USERS_TABLE);
  };

  const onSubmit = async (data: TCreateInstitutionForm) => {
    const { provisioning, ...rest } = data;

    try {
      const created = await createInstitution({
        ...rest,
        ...(currentOrganizationId && { organizationId: currentOrganizationId }),
        ...(isInstitutionProvisioning && {
          provisioning: {
            contracts: provisioning.contracts.map(
              ({
                seats,
                stationLicenses,
                contractStartDate,
                contractEndDate,
              }) => ({
                seats: Number(seats),
                stationLicenses: Number(stationLicenses),
                contractStartDate,
                contractEndDate,
              }),
            ),
          },
        }),
      });
      setCurrentInstitutionById(created.id);
      setStep('success');
    } catch (error) {
      if (
        isAxiosError(error) &&
        error.response?.data?.error === `${EServerError.BAD_PARAMS}:name`
      ) {
        setCurrentTab('profile');
        setError('name', {
          type: 'server',
          message: 'admin-panel.institution-name-already-exists',
        });
        return;
      }
      setToast({
        title: t('something-went-wrong-please-try-again'),
        variant: 'error',
      });
      console.error('Create institution failed:', error);
    }
  };

  return (
    <>
      <Modal
        open={open && !isDiscardOpen}
        onOpenChange={
          step === 'form'
            ? (nextOpen) => {
                if (!nextOpen) handleCloseAttempt();
              }
            : undefined
        }
        title={
          step === 'form' ? (
            <div className={styles.title}>
              <Icon
                name="Hospital"
                size="24"
                color="var(--colors-primary600)"
              />
              <Typography fontSize="20px" fontWeight={600}>
                {t('admin-panel.create-institution-title')}
              </Typography>
            </div>
          ) : (
            ''
          )
        }
        sx={{ width: step === 'form' ? '542px' : '446px' }}
        body={
          step === 'form' ? (
            <FormProvider {...formMethods}>
              <form onSubmit={handleSubmit(onSubmit)} className={styles.body}>
                <div className={styles.tabsWrapper}>
                  <Tabs
                    items={items}
                    activeKey={currentTab}
                    onChange={(value) => {
                      if (isCreateInstitutionTab(value.key))
                        setCurrentTab(value.key);
                    }}
                    variant="underline"
                  />
                </div>
                <div className={styles.tabContent}>
                  {currentTab === 'profile' && <ProfileTab />}
                  {currentTab === 'poc' && <PocTab />}
                  {currentTab === 'provisioning' &&
                    isInstitutionProvisioning && <ProvisioningTab />}
                </div>
                <div className={styles.buttons}>
                  <Button
                    text={t('nav-panel.create-institution')}
                    preIcon="Plus"
                    isSubmit
                    disabled={!!invalidFields.size || isSubmitting}
                    loading={isSubmitting}
                  />
                </div>
              </form>
            </FormProvider>
          ) : (
            <div className={styles.successBody}>
              <Icon name="CheckCircle" color="green" size="48" />
              <Typography fontSize="20px" fontWeight={600}>
                {t('admin-panel.create-institution-success-title')}
              </Typography>
              <Typography
                textAlign="center"
                sx={{ lineHeight: '20px' }}
                mb="12px"
                color="var(--colors-secondary)"
              >
                <Trans
                  i18nKey="admin-panel.create-institution-success-body"
                  components={{ bold: <strong /> }}
                />
              </Typography>
              <Button
                text={t('encounter.get-started')}
                variant="secondary"
                onClick={handleGetStarted}
                className={styles.successButton}
                wide
              />
            </div>
          )
        }
      />
      <CloseWarningModal
        isOpen={isDiscardOpen}
        description={t('admin-panel.discard-institution-body')}
        onCancel={() => setIsDiscardOpen(false)}
        onConfirm={() => {
          setIsDiscardOpen(false);
          resetAndClose();
        }}
      />
    </>
  );
};
